import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { calculateAverageSale } from '@/lib/analytics/metricsCalculations'
import type { MetricPeriod } from '@/lib/utils'
import type { MonthMetrics, MonthPlanFact } from '@/features/history/types'

function buildAverageCheckMetric(
  planAmount: number,
  planCount: number,
  factAmount: number,
  factCount: number
): MonthPlanFact {
  const plan = calculateAverageSale(planAmount, planCount)
  const fact = calculateAverageSale(factAmount, factCount)
  return { plan, fact, completion: plan > 0 ? (fact / plan) * 100 : 0 }
}

export function useHistoryMetrics(
  studioId: number,
  period: MetricPeriod | null
): { metrics: MonthMetrics | null; loading: boolean } {
  const [metrics, setMetrics] = useState<MonthMetrics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!studioId || !period) return
    load()

    async function load() {
      setLoading(true)
      try {
        const [weeklyResult, totalsResult] = await Promise.all([
          supabase
            .from('studio_weekly_plan_vs_fact')
            .select('*')
            .eq('studio_internal_id', studioId)
            .eq('period_id', period!.period_id)
            .order('week_number_in_period') as unknown as Promise<{ data: any[] | null; error: any }>,
          supabase
            .from('studio_period_totals')
            .select('last_total_clients_count')
            .eq('studio_internal_id', studioId)
            .eq('period_id', period!.period_id)
            .maybeSingle() as unknown as Promise<{
              data: { last_total_clients_count: number | null } | null
              error: any
            }>,
        ])

        if (weeklyResult.error) throw weeklyResult.error
        if (totalsResult.error) console.warn('period totals error:', totalsResult.error)

        const weeklyRows = weeklyResult.data
        if (!weeklyRows || weeklyRows.length === 0) {
          setMetrics(null)
          return
        }

        const sum = (field: string) =>
          weeklyRows.reduce((acc, row) => acc + (Number(row[field]) || 0), 0)

        const firstNonNull = (field: string) =>
          weeklyRows.find((r) => r[field] != null)?.[field] ?? 0

        const pct = (plan: number, fact: number) =>
          plan > 0 ? (fact / plan) * 100 : 0

        const leadsPlan           = sum('weekly_leads_plan')
        const leadsFact           = sum('weekly_leads_fact')
        const appointmentsPlan    = sum('weekly_appointments_plan')
        const appointmentsFact    = sum('weekly_appointments_fact')
        const visitorsPlan        = sum('weekly_new_visitors_plan')
        const visitorsFact        = sum('weekly_new_visitors_fact')
        const newSalesPlan        = sum('weekly_new_sales_plan')
        const newSalesFact        = sum('weekly_new_sales_fact')
        const newSalesAmountPlan  = sum('weekly_new_sales_amount_plan')
        const newSalesAmountFact  = sum('weekly_new_sales_amount_fact')
        const renewalsPlan        = sum('weekly_renewals_count_plan')
        const renewalsFact        = sum('weekly_renewals_count_fact')
        const renewalsAmountPlan  = sum('weekly_renewals_amount_plan')
        const renewalsAmountFact  = sum('weekly_renewals_amount_fact')
        const upgradesPlan        = sum('weekly_upgrades_count_plan')
        const upgradesFact        = sum('weekly_upgrades_count_fact')
        const upgradesAmountPlan  = sum('weekly_upgrades_amount_plan')
        const upgradesAmountFact  = sum('weekly_upgrades_amount_fact')
        const returningPlan       = sum('weekly_returning_clients_count_plan')
        const returningFact       = sum('weekly_returning_clients_count_fact')
        const returningAmountPlan = sum('weekly_returning_clients_amount_plan')
        const returningAmountFact = sum('weekly_returning_clients_amount_fact')
        const privatePlan         = sum('weekly_private_sessions_count_plan')
        const privateFact         = sum('weekly_private_sessions_count_fact')
        const privateAmountPlan   = sum('weekly_private_sessions_amount_plan')
        const privateAmountFact   = sum('weekly_private_sessions_amount_fact')
        const newSubsPlan         = sum('weekly_new_subscriptions_count_plan')
        const newSubsFact         = sum('weekly_new_subscriptions_count_fact')
        const newSubsAmountPlan   = sum('weekly_new_subscriptions_amount_plan')
        const newSubsAmountFact   = sum('weekly_new_subscriptions_amount_fact')
        const mfNewPlan           = sum('weekly_membership_fees_new_plan')
        const mfUpgradesPlan      = sum('weekly_membership_fees_subscription_upgrades_plan')
        const mfReturningPlan     = sum('weekly_membership_fees_subscription_returning_plan')
        const subRenewalsPlan     = sum('weekly_subscription_renewals_count_plan')
        const subRenewalsFact     = sum('weekly_subscription_renewals_count_fact')
        const subRenewalsAmtPlan  = sum('weekly_subscription_renewals_amount_plan')
        const subRenewalsAmtFact  = sum('weekly_subscription_renewals_amount_fact')
        const subUpgradesPlan     = sum('weekly_subscription_upgrades_count_plan')
        const subUpgradesFact     = sum('weekly_subscription_upgrades_count_fact')
        const subUpgradesAmtPlan  = sum('weekly_subscription_upgrades_amount_plan')
        const subUpgradesAmtFact  = sum('weekly_subscription_upgrades_amount_fact')
        const subReturningPlan    = sum('weekly_subscription_returning_clients_count_plan')
        const subReturningFact    = sum('weekly_subscription_returning_clients_count_fact')
        const subReturningAmtPlan = sum('weekly_subscription_returning_clients_amount_plan')
        const subReturningAmtFact = sum('weekly_subscription_returning_clients_amount_fact')
        const marketingBudgetPlan = sum('weekly_marketing_budget_plan')
        const marketingBudgetFact = sum('weekly_marketing_expenses_amount_fact')
        const grossRevenuePlan    = sum('weekly_gross_revenue_plan')
        const grossRevenueFact    = sum('weekly_gross_revenue_fact')
        const refundsPlan         = sum('weekly_refunds_amount_plan')
        const refundsFact         = sum('weekly_refunds_amount_fact')
        const trialPaymentsFact   = sum('weekly_trial_payments_amount_fact')
        const otherIncomeFact     = sum('weekly_other_income_fact')
        const mfNewFact           = sum('weekly_membership_fees_new_amount_fact')
        const mfUpgradesFact      = sum('weekly_membership_fees_subscription_upgrades_amount_fact')
        const mfReturningFact     = sum('weekly_membership_fees_subscription_returning_amount_fact')

        setMetrics({
          clients_at_start: {
            plan: Number(firstNonNull('weekly_decomposed_clients_plan')) || 0,
            fact: 0,
            completion: 0,
          },
          clients_at_end: {
            fact: Number(totalsResult.data?.last_total_clients_count) || 0,
          },

          marketing_budget: { plan: marketingBudgetPlan, fact: marketingBudgetFact, completion: pct(marketingBudgetPlan, marketingBudgetFact) },
          cost_per_lead: {
            plan: leadsPlan > 0 ? marketingBudgetPlan / leadsPlan : 0,
            fact: leadsFact > 0 ? marketingBudgetFact / leadsFact : 0,
            completion: 0,
          },

          leads_count:        { plan: leadsPlan,        fact: leadsFact,        completion: pct(leadsPlan, leadsFact) },
          appointments_count: { plan: appointmentsPlan, fact: appointmentsFact, completion: pct(appointmentsPlan, appointmentsFact) },
          new_visitors_count: { plan: visitorsPlan,     fact: visitorsFact,     completion: pct(visitorsPlan, visitorsFact) },

          new_sales_count:     { plan: newSalesPlan,       fact: newSalesFact,       completion: pct(newSalesPlan, newSalesFact) },
          new_sales_amount:    { plan: newSalesAmountPlan,  fact: newSalesAmountFact,  completion: pct(newSalesAmountPlan, newSalesAmountFact) },
          new_sales_avg_check: buildAverageCheckMetric(newSalesAmountPlan, newSalesPlan, newSalesAmountFact, newSalesFact),
          trial_payments: { fact: trialPaymentsFact },

          renewals_count:     { plan: renewalsPlan,       fact: renewalsFact,       completion: pct(renewalsPlan, renewalsFact) },
          renewals_amount:    { plan: renewalsAmountPlan,  fact: renewalsAmountFact,  completion: pct(renewalsAmountPlan, renewalsAmountFact) },
          renewals_avg_check: buildAverageCheckMetric(renewalsAmountPlan, renewalsPlan, renewalsAmountFact, renewalsFact),

          upgrades_count:     { plan: upgradesPlan,       fact: upgradesFact,       completion: pct(upgradesPlan, upgradesFact) },
          upgrades_amount:    { plan: upgradesAmountPlan,  fact: upgradesAmountFact,  completion: pct(upgradesAmountPlan, upgradesAmountFact) },
          upgrades_avg_check: buildAverageCheckMetric(upgradesAmountPlan, upgradesPlan, upgradesAmountFact, upgradesFact),

          returning_clients_count:     { plan: returningPlan,       fact: returningFact,       completion: pct(returningPlan, returningFact) },
          returning_clients_amount:    { plan: returningAmountPlan,  fact: returningAmountFact,  completion: pct(returningAmountPlan, returningAmountFact) },
          returning_clients_avg_check: buildAverageCheckMetric(returningAmountPlan, returningPlan, returningAmountFact, returningFact),

          private_sessions_count:     { plan: privatePlan,       fact: privateFact,       completion: pct(privatePlan, privateFact) },
          private_sessions_amount:    { plan: privateAmountPlan,  fact: privateAmountFact,  completion: pct(privateAmountPlan, privateAmountFact) },
          private_sessions_avg_check: buildAverageCheckMetric(privateAmountPlan, privatePlan, privateAmountFact, privateFact),

          membership_fees_new:                 { plan: mfNewPlan,      fact: mfNewFact,      completion: pct(mfNewPlan, mfNewFact) },
          membership_fees_subscription_upgrades: { plan: mfUpgradesPlan, fact: mfUpgradesFact, completion: pct(mfUpgradesPlan, mfUpgradesFact) },
          membership_fees_subscription_returning:{ plan: mfReturningPlan,fact: mfReturningFact,completion: pct(mfReturningPlan, mfReturningFact) },

          new_subscriptions_count:     { plan: newSubsPlan,       fact: newSubsFact,       completion: pct(newSubsPlan, newSubsFact) },
          new_subscriptions_amount:    { plan: newSubsAmountPlan,  fact: newSubsAmountFact,  completion: pct(newSubsAmountPlan, newSubsAmountFact) },
          new_subscriptions_avg_check: buildAverageCheckMetric(newSubsAmountPlan, newSubsPlan, newSubsAmountFact, newSubsFact),

          subscription_renewals_count:     { plan: subRenewalsPlan,    fact: subRenewalsFact,    completion: pct(subRenewalsPlan, subRenewalsFact) },
          subscription_renewals_amount:    { plan: subRenewalsAmtPlan,  fact: subRenewalsAmtFact,  completion: pct(subRenewalsAmtPlan, subRenewalsAmtFact) },
          subscription_renewals_avg_check: buildAverageCheckMetric(subRenewalsAmtPlan, subRenewalsPlan, subRenewalsAmtFact, subRenewalsFact),

          subscription_upgrades_count:     { plan: subUpgradesPlan,    fact: subUpgradesFact,    completion: pct(subUpgradesPlan, subUpgradesFact) },
          subscription_upgrades_amount:    { plan: subUpgradesAmtPlan,  fact: subUpgradesAmtFact,  completion: pct(subUpgradesAmtPlan, subUpgradesAmtFact) },
          subscription_upgrades_avg_check: buildAverageCheckMetric(subUpgradesAmtPlan, subUpgradesPlan, subUpgradesAmtFact, subUpgradesFact),

          subscription_returning_clients_count:     { plan: subReturningPlan,    fact: subReturningFact,    completion: pct(subReturningPlan, subReturningFact) },
          subscription_returning_clients_amount:    { plan: subReturningAmtPlan,  fact: subReturningAmtFact,  completion: pct(subReturningAmtPlan, subReturningAmtFact) },
          subscription_returning_clients_avg_check: buildAverageCheckMetric(subReturningAmtPlan, subReturningPlan, subReturningAmtFact, subReturningFact),

          other_income: { plan: 0, fact: otherIncomeFact, completion: 0 },
          refunds:      { plan: refundsPlan, fact: refundsFact, completion: pct(Math.abs(refundsPlan), Math.abs(refundsFact)) },
          gross_revenue:{ plan: grossRevenuePlan, fact: grossRevenueFact, completion: pct(grossRevenuePlan, grossRevenueFact) },
        })
      } catch (err) {
        console.error('useHistoryMetrics error:', err)
        setMetrics(null)
      } finally {
        setLoading(false)
      }
    }
  }, [studioId, period?.period_id])

  return { metrics, loading }
}
